import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { equipmentPageData, lineUrl } from "@/mocks/siteData";
import SEO from "@/components/base/SEO";

function useRevealOnScroll() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("opacity-100", "translate-y-0");
            entry.target.classList.remove("opacity-0", "translate-y-6");
          }
        });
      },
      { threshold: 0.1 }
    );
    el.querySelectorAll(".reveal").forEach((child) => observer.observe(child));
    return () => observer.disconnect();
  }, []);
  return ref;
}

export default function Equipment() {
  const revealRef = useRevealOnScroll();
  const data = equipmentPageData;

  return (
    <>
      <SEO
        title="醫美儀器介紹｜時光逆行駭客｜專業設備與療程選擇"
        description="醫美儀器不是答案，正確評估才是逆齡的開始。了解膚質與光療、注射微整、拉提緊緻、亮白保養與體態管理類儀器，以及儀器選擇原則。"
        keywords="醫美儀器,皮秒雷射,音波拉提,電波拉提,玻尿酸,肉毒,時光逆行駭客"
        canonicalPath="/equipment"
        schema={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "醫美儀器介紹｜時光逆行駭客",
          "description": "了解各類醫美儀器與療程選擇原則，先評估再決定，不是越貴越適合。",
        }}
      />
    <div className="min-h-screen bg-abyss-900 circuit-bg" ref={revealRef}>
      {/* ===== HERO ===== */}
      <section className="relative pt-28 md:pt-36 pb-16 md:pb-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-hero" />
        <div className="absolute inset-0 circuit-bg opacity-20" />
        <div className="relative z-10 mx-auto px-4 md:px-6 lg:px-8 w-full" style={{ maxWidth: 1280 }}>
          <div className="text-center max-w-4xl mx-auto">
            <span className="section-label reveal opacity-0 translate-y-6 transition-all duration-700">
              {data.heroTagline}
            </span>
            <h1 className="mt-5 text-[2rem] leading-[1.3] md:text-[2.75rem] md:leading-[1.25] font-bold text-ghost reveal opacity-0 translate-y-6 transition-all duration-700 delay-100">
              {data.heroTitle}
              <br />
              <span className="text-gradient-gold">{data.heroTitleAccent}</span>
            </h1>
            <p className="mt-6 text-ghost-muted text-[1.1875rem] leading-[1.75] reveal opacity-0 translate-y-6 transition-all duration-700 delay-200">
              {data.heroDesc}
            </p>
          </div>
        </div>
      </section>

      {/* ===== TREATMENT CATEGORIES ===== */}
      <section className="py-20 md:py-28 bg-abyss-800/50">
        <div className="mx-auto px-4 md:px-6 lg:px-8 w-full" style={{ maxWidth: 1280 }}>
          <div className="text-center mb-12 md:mb-16 reveal opacity-0 translate-y-6 transition-all duration-700">
            <span className="section-label">{data.categories.label}</span>
            <h2 className="mt-4 text-[1.5rem] leading-[1.4] md:text-[2.125rem] md:leading-[1.35] font-bold text-ghost">
              {data.categories.title}
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {data.categories.items.map((cat, idx) => (
              <div
                key={idx}
                className="card-cyber p-6 md:p-8 reveal opacity-0 translate-y-6 transition-all duration-700"
                style={{ transitionDelay: `${idx * 100}ms` }}
              >
                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5"
                  style={{ background: `${cat.accent}10`, border: `1px solid ${cat.accent}20` }}
                >
                  <i className={`${cat.icon} text-2xl`} style={{ color: cat.accent }} />
                </div>
                <h3 className="text-[1.125rem] md:text-[1.25rem] font-semibold text-ghost mb-4">
                  {cat.title}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {cat.items.map((item) => (
                    <span
                      key={item}
                      className="px-3 py-1.5 rounded-lg text-base font-medium"
                      style={{
                        background: `${cat.accent}08`,
                        color: cat.accent,
                        border: `1px solid ${cat.accent}18`,
                      }}
                    >
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== EQUIPMENT EXAMPLES ===== */}
      <section className="py-20 md:py-28">
        <div className="mx-auto px-4 md:px-6 lg:px-8 w-full" style={{ maxWidth: 1280 }}>
          <div className="text-center mb-10 md:mb-12 reveal opacity-0 translate-y-6 transition-all duration-700">
            <span className="section-label">{data.examples.label}</span>
            <h2 className="mt-4 text-[1.5rem] leading-[1.4] md:text-[2.125rem] md:leading-[1.35] font-bold text-ghost">
              {data.examples.title}
            </h2>
            <p className="mt-3 text-ghost-dim text-[1.0625rem]">{data.examples.subtitle}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.examples.items.map((item, idx) => (
              <div
                key={idx}
                className="card-cyber p-5 md:p-6 flex items-center gap-4 reveal opacity-0 translate-y-6 transition-all duration-700"
                style={{ transitionDelay: `${idx * 80}ms` }}
              >
                <div className="w-10 h-10 rounded-xl bg-cyber-gold/8 border border-cyber-gold/15 flex items-center justify-center shrink-0">
                  <i className="ri-cpu-line text-cyber-gold text-lg" />
                </div>
                <span className="text-ghost text-[1.0625rem] font-medium">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== SELECTION PRINCIPLES ===== */}
      <section className="py-20 md:py-28 bg-abyss-800/50">
        <div className="mx-auto px-4 md:px-6 lg:px-8 w-full" style={{ maxWidth: 1280 }}>
          <div className="text-center mb-12 md:mb-16 reveal opacity-0 translate-y-6 transition-all duration-700">
            <span className="section-label">{data.principles.label}</span>
            <h2 className="mt-4 text-[1.5rem] leading-[1.4] md:text-[2.125rem] md:leading-[1.35] font-bold text-ghost">
              {data.principles.title}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {data.principles.cards.map((card, idx) => (
              <div
                key={idx}
                className="card-cyber p-6 md:p-8 reveal opacity-0 translate-y-6 transition-all duration-700"
                style={{ transitionDelay: `${idx * 100}ms` }}
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-cyber-gold/10 border border-cyber-gold/20 flex items-center justify-center shrink-0">
                    <i className={`${card.icon} text-cyber-gold text-xl`} />
                  </div>
                  <div>
                    <h3 className="text-[1.125rem] md:text-[1.25rem] font-semibold text-ghost mb-2">
                      {card.title}
                    </h3>
                    <p className="text-ghost-muted text-[1.0625rem] leading-[1.7]">{card.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== COMPLIANCE REMINDER ===== */}
      <section className="py-12 md:py-16">
        <div className="mx-auto px-4 md:px-6 lg:px-8 w-full" style={{ maxWidth: 1280 }}>
          <div className="reveal opacity-0 translate-y-6 transition-all duration-700">
            <div className="card-cyber p-6 md:p-8 border-l-4 border-l-cyber-gold/30">
              <div className="flex items-start gap-3">
                <i className="ri-information-line text-cyber-gold text-xl shrink-0 mt-0.5" />
                <p className="text-ghost-dim text-[1.0625rem] leading-[1.7]">
                  {data.compliance}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== BOTTOM CTA ===== */}
      <section className="py-20 md:py-28">
        <div className="mx-auto px-4 md:px-6 lg:px-8 w-full" style={{ maxWidth: 1280 }}>
          <div className="card-cyber p-10 md:p-16 text-center reveal opacity-0 translate-y-6 transition-all duration-700">
            <span className="section-label">{data.bottomCta.label}</span>
            <h2 className="mt-5 text-[1.5rem] leading-[1.4] md:text-[2.125rem] md:leading-[1.35] font-bold text-ghost">
              {data.bottomCta.title}
            </h2>
            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href={lineUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-line text-[1.0625rem]"
              >
                <i className="ri-line-fill" />
                {data.bottomCta.ctaLine}
              </a>
              <Link to="/booking" className="btn-gold text-[1.0625rem]">
                <i className="ri-calendar-check-line" />
                {data.bottomCta.ctaBooking}
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  </>
  );
}